<script setup lang="ts">
import type { ModalProps } from '@nuxt/ui'

interface FullScreenProps {
  title?: string
  description?: string
  fullscreen?: boolean
  noEscape?: boolean
  noHeader?: boolean
  ui?: ModalProps['ui']
}
const props = defineProps<FullScreenProps>()

const open = defineModel<boolean>('open', { default: true })
const emit = defineEmits(['close'])
const system = useSystemStore()
const modalId = system.getModal()

if (props.fullscreen)
  useBodyScrollLock(() => open.value)

onMounted(() => {
  system.pushModal(modalId)
})

onBeforeUnmount(() => {
  system.popModal(modalId)
})

const onUpdateOpen = (next: boolean): void => {
  if (!open.value)
    return

  if (!system.isTopModal(modalId)) {
    open.value = true
    return
  }
  if (!next && open.value) {
    open.value = false
    emit('close')
  }
}

defineShortcuts({
  escape: () => {
    if (props.noEscape)
      return
    onUpdateOpen(false)
  }
})

const slots = useSlots()

const noFull: ModalProps['ui'] = {
  content: 'w-full sm:max-w-xl md:max-w-2xl lg:max-w-6xl',
  header: 'flex items-center gap-1.5 pb-2 sm:px-4 min-h-16',
  body: 'flex-1 p-4 sm:p-4',
  description: 'mt-2',
  footer: !slots.footer ? 'sr-only' : 'p-2 sm:pb-4 sm:px-4'
}
const full: ModalProps['ui'] = {
  content: 'w-full h-full overflow-hidden',
  description: 'mt-2',
  body: 'overflow-hidden p-0!',
  header: props.noHeader ? 'sr-only' : 'p-2 sm:pt-4 sm:px-4',
  footer: !slots.footer ? 'sr-only' : 'p-2 sm:pb-4 sm:px-4'
}
</script>

<template>
  <UModal
    v-model:open="open"
    :title
    :description
    :fullscreen
    :ui="ui ? ui : fullscreen ? full : noFull"
    @update:open="onUpdateOpen"
  >
    <template #title>
      <slot name="title" />
    </template>
    <template #body>
      <BaseGridLayout
        v-if="slots.grid"
        class="h-full"
      >
        <slot />
        <template #grid>
          <slot name="grid" />
        </template>
      </BaseGridLayout>
      <slot v-else />
    </template>
    <template #footer>
      <slot name="footer" />
    </template>
  </UModal>
</template>
